import * as React from 'react';
import { InputOTP, type InputOTPProps } from './input-otp';
import { Button } from './button';
import { TextLink } from './link';
import { cn } from '../lib/utils';

type ClipboardTextGetter = NonNullable<InputOTPProps['getClipboardText']>;

export type EmailOTPProps = {
  title: string;
  subtitle?: string;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  maxLength?: number;
  disabled?: boolean;
  otpAriaLabel?: string;
  helperText?: string;
  errorText?: string;
  successText?: string;
  resendLabel: string;
  resendLoading?: boolean;
  onResend: () => void;
  resendDisabled?: boolean;
  resendHint?: string;
  backLabel?: string;
  onBack?: () => void;
  showPasteButton?: boolean;
  pasteButtonLabel?: string;
  pasteButtonIcon?: React.ReactNode;
  clearButtonLabel?: string;
  clearButtonIcon?: React.ReactNode;
  getClipboardText?: ClipboardTextGetter;
  otpInputTestId?: string;
  resendTestId?: string;
  backTestId?: string;
  className?: string;
};

export function EmailOTP({
  title,
  subtitle,
  value,
  onChange,
  onComplete,
  maxLength,
  disabled = false,
  otpAriaLabel,
  helperText,
  errorText,
  successText,
  resendLabel,
  resendLoading = false,
  onResend,
  resendDisabled = false,
  resendHint,
  backLabel,
  onBack,
  showPasteButton = true,
  pasteButtonLabel,
  pasteButtonIcon,
  clearButtonLabel,
  clearButtonIcon,
  getClipboardText,
  otpInputTestId,
  resendTestId,
  backTestId,
  className,
}: EmailOTPProps) {
  const showBackLink = Boolean(backLabel && onBack);

  const toneClass = errorText
    ? 'text-destructive'
    : successText
      ? 'text-status-success'
      : 'text-muted-foreground';
  const feedbackMessage = errorText ?? successText ?? helperText;
  const resendDisplayLabel =
    resendHint && resendDisabled ? `${resendLabel} (${resendHint})` : resendLabel;

  return (
    <section className={cn('w-full max-w-md space-y-4', className)}>
      <header className="space-y-1 text-center">
        <h2 className="text-xl font-semibold text-foreground">{title}</h2>
        {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
      </header>

      <div className="flex justify-center">
        <InputOTP
          value={value}
          onChange={onChange}
          onComplete={onComplete}
          maxLength={maxLength}
          disabled={disabled}
          ariaLabel={otpAriaLabel}
          showPasteButton={showPasteButton}
          pasteButtonLabel={pasteButtonLabel}
          pasteButtonIcon={pasteButtonIcon}
          showClearButtonWhenFilled={Boolean(errorText)}
          clearButtonLabel={clearButtonLabel}
          clearButtonIcon={clearButtonIcon}
          getClipboardText={getClipboardText}
          data-testid={otpInputTestId}
        />
      </div>

      {feedbackMessage ? <p className={cn('text-sm text-center', toneClass)}>{feedbackMessage}</p> : null}

      <div className="flex justify-center">
        <Button
          type="button"
          variant="default"
          size="sm"
          loading={resendLoading}
          disabled={resendDisabled || disabled || resendLoading}
          onClick={onResend}
          data-testid={resendTestId}
        >
          {resendDisplayLabel}
        </Button>
      </div>

      {showBackLink ? (
        <div className="flex justify-center">
          <TextLink asChild variant="secondary" size="sm">
            <button type="button" onClick={onBack} data-testid={backTestId} className="w-fit">
              {backLabel}
            </button>
          </TextLink>
        </div>
      ) : null}
    </section>
  );
}
